import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import ReactSelect from './ReactSelect';
import { toast } from 'react-toastify';
import { FaChevronDown, FaChevronUp, FaClipboardList, FaDownload, FaPaperPlane, FaPlus, FaTimes, FaTrash } from 'react-icons/fa';
import { data } from '../util/util';

export default function ContratacaoForm({ eventoId, onClose, canEdit = true }) {
    const [initialLoading, setInitialLoading] = useState(true);
    const [loading, setLoading] = useState(false);
    const [eventoData, setEventoData] = useState({ nome: '', data: '', dataFim: '', hora: '' });
    const [categories, setCategories] = useState([]);
    const [freelancers, setFreelancers] = useState([]);
    const [selections, setSelections] = useState({});
    const [expandedFreelancers, setExpandedFreelancers] = useState({});
    const [uploadingNf, setUploadingNf] = useState({});

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        if (dateStr.includes('/')) return dateStr;
        const [year, month, day] = dateStr.split('-');
        return `${day}/${month}/${year}`;
    };

    const buscarDadosEvento = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await api({
                method: 'get',
                action: `/evento/${eventoId}`,
                token
            });
            if (response.data.status && response.data.data) {
                setEventoData({
                    nome: response.data.data.nome || '',
                    data: data(response.data.data.data) || '',
                    dataFim: data(response.data.data.dataFim) || '',
                    hora: response.data.data.hora || ''
                });
            }
        } catch (error) {
            console.log('Erro ao buscar dados do evento:', error);
        }
    }, [eventoId]);

    const buscarFreelancers = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await api({
                method: 'get',
                action: '/participante',
                token,
                params: {
                    tipoCadastro: ['freelancer', 'pessoa'],
                    orderBy: { tipoCadastro: 'asc' }
                }
            });
            setFreelancers(response.data.data ?? []);
        } catch (error) {
            console.log('Erro ao buscar freelancers:', error);
        }
    }, []);

    const buscarContratacoes = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await api({
                method: 'get',
                action: `/eventoFreelancerParticipante/${eventoId}`,
                token
            });

            if (response.data.status && Array.isArray(response.data.data)) {
                const categoriasConvertidas = response.data.data.map((cat) => ({
                    ...cat,
                    especialidade: cat.especialidade ? {
                        ...cat.especialidade,
                        label: cat.especialidade.nome,
                    } : null,
                    quantidade_freelancers: cat.quantidade_freelancers || 0,
                    freelancers_indisponiveis: (cat.freelancers_indisponiveis ?? []).map((id) => String(id)),
                }));
                setCategories(categoriasConvertidas);

                const novasSelecoes = {};

                response.data.data.forEach((categoria) => {
                    const categoryKey = String(categoria.id ?? '');
                    if (!categoryKey) {
                        return;
                    }

                    (categoria.freelancers ?? []).forEach((freelancer, slotIndex) => {
                        const participanteId = freelancer?.participante ?? freelancer;
                        const ordem = parseInt(freelancer?.ordem ?? (slotIndex + 1), 10);
                        const ordemIndex = Number.isNaN(ordem) || ordem < 1 ? slotIndex : (ordem - 1);

                        if (participanteId !== null && participanteId !== undefined && participanteId !== '') {
                            novasSelecoes[`${categoryKey}-${ordemIndex}`] = String(participanteId);
                        }
                    });
                });

                setSelections(novasSelecoes);
            } else {
                setCategories([]);
                setSelections({});
            }
        } catch (error) {
            console.log('Erro ao buscar contratações:', error);
            setCategories([]);
            setSelections({});
        }
    }, [eventoId]);

    useEffect(() => {
        const loadData = async () => {
            await Promise.all([
                buscarDadosEvento(),
                buscarFreelancers(),
                buscarContratacoes()
            ]);
            setInitialLoading(false);
        };
        loadData();
    }, [buscarDadosEvento, buscarFreelancers, buscarContratacoes]);

    const getCategoryKey = (category, index) => {
        return String(category?.id ?? index);
    };

    const handleSelectionChange = (categoryKey, slotIndex, value, quantidadeEsperada = 0, category = null) => {
        if (!canEdit) {
            return;
        }

        const key = `${categoryKey}-${slotIndex}`;
        const currentValue = selections[key];

        if (currentValue && String(currentValue) !== String(value || '')) {
            const contratacao = (category?.freelancers ?? []).find((freelancer) => {
                return String(freelancer.participante) === String(currentValue);
            });

            if ((contratacao?.arquivos_nota_fiscal ?? []).length > 0) {
                toast.error('Remova as notas fiscais antes de remover este freelancer.');
                return;
            }
        }

        setSelections(prev => {
            if (value) {
                const next = { ...prev };
                next[key] = value;
                return next;
            }

            const categoryIndexes = Object.keys(prev)
                .filter((currentKey) => currentKey.startsWith(`${categoryKey}-`))
                .map((currentKey) => parseInt(currentKey.split('-').pop(), 10))
                .filter((currentIndex) => !Number.isNaN(currentIndex));

            const quantidadeAtualSlots = categoryIndexes.length > 0 ? Math.max(...categoryIndexes) + 1 : 0;
            const deveCompactar = quantidadeAtualSlots > quantidadeEsperada;

            if (!deveCompactar) {
                const next = { ...prev };
                delete next[key];
                return next;
            }

            const next = Object.fromEntries(
                Object.entries(prev).filter(([currentKey]) => !currentKey.startsWith(`${categoryKey}-`))
            );

            const remainingValues = Object.entries(prev)
                .filter(([currentKey, currentValue]) => currentKey.startsWith(`${categoryKey}-`) && currentKey !== key && currentValue)
                .sort((a, b) => {
                    const indexA = parseInt(a[0].split('-').pop(), 10) || 0;
                    const indexB = parseInt(b[0].split('-').pop(), 10) || 0;

                    return indexA - indexB;
                })
                .map(([, currentValue]) => currentValue);

            remainingValues.forEach((currentValue, index) => {
                next[`${categoryKey}-${index}`] = currentValue;
            });

            return next;
        });
    };

    const isFreelancerSelectedElsewhere = (participanteId, currentCategoryKey, currentSlotIndex) => {
        if (!participanteId) return false;
        return Object.entries(selections).some(([key, val]) => {
            return val === participanteId && key !== `${currentCategoryKey}-${currentSlotIndex}`;
        });
    };

    const getTotalFreelancers = () => {
        return categories.reduce((acc, cat) => acc + (parseInt(cat.quantidade_freelancers) || 0), 0);
    };

    const getCategoryLetter = (index) => String.fromCharCode(65 + index);
    const getCategoryTitle = (category, index) => `Categoria ${getCategoryLetter(index)}: ${category.especialidade?.label || '-'}`;

    const getCategoryPeriod = (category) => {
        const inicio = [category.data_in, category.hora_in].filter(Boolean).join(' ');
        const fim = [category.data_out, category.hora_out].filter(Boolean).join(' ');

        if (!inicio && !fim) {
            return '-';
        }

        return `${inicio || '-'} - ${fim || '-'}`;
    };

    const isFreelancerUnavailableForCategory = (participanteId, category) => {
        if (!participanteId || !category) {
            return false;
        }

        return (category.freelancers_indisponiveis ?? []).includes(String(participanteId));
    };

    const getFreelancerOptions = (category, categoryKey, slotIndex) => {
        const especialidadeId = String(category?.especialidade?.id ?? '');

        return [...freelancers].sort((a, b) => {
            const aMatch = (a.especialidades ?? []).some(e => String(e.id) === especialidadeId);
            const bMatch = (b.especialidades ?? []).some(e => String(e.id) === especialidadeId);

            return Number(bMatch) - Number(aMatch);
        }).map((freelancer) => {
            const participanteId = String(freelancer.id);
            const isInactive = freelancer.situacaoRegistro?.toLowerCase() === 'inativo';
            const isUnavailable = isFreelancerUnavailableForCategory(participanteId, category);
            const isAlreadySelected = isFreelancerSelectedElsewhere(participanteId, categoryKey, slotIndex);
            const statusLabel = isInactive
                ? 'INATIVO'
                : isUnavailable
                    ? 'INDISPONÍVEL'
                    : isAlreadySelected
                        ? 'JÁ SELECIONADO'
                        : null;
            const statusClassName = isInactive
                ? 'text-danger'
                : isUnavailable
                    ? 'text-warning'
                    : isAlreadySelected
                        ? 'text-muted'
                        : '';

            return {
                value: participanteId,
                label: freelancer.nome,
                baseLabel: freelancer.nome,
                statusLabel,
                statusClassName,
                disabled: isInactive || isUnavailable || isAlreadySelected,
            };
        });
    };

    const getSelectedFreelancerOption = (categoryKey, slotIndex, options) => {
        const selectedValue = selections[`${categoryKey}-${slotIndex}`];

        if (!selectedValue) {
            return null;
        }

        return options.find((option) => option.value === String(selectedValue)) || null;
    };

    const getFreelancerContratacao = (category, categoryKey, slotIndex) => {
        const selectedValue = selections[`${categoryKey}-${slotIndex}`];

        if (!selectedValue) {
            return null;
        }

        return (category.freelancers ?? []).find((freelancer) => {
            return String(freelancer.participante) === String(selectedValue);
        }) || null;
    };

    const updateContratacaoArquivos = (contratacaoId, arquivosNotaFiscal) => {
        setCategories((prev) => prev.map((category) => ({
            ...category,
            freelancers: (category.freelancers ?? []).map((freelancer) => (
                String(freelancer.id) === String(contratacaoId)
                    ? { ...freelancer, arquivos_nota_fiscal: arquivosNotaFiscal ?? [] }
                    : freelancer
            )),
        })));
    };

    const toggleFreelancerExpanded = (slotKey) => {
        setExpandedFreelancers((prev) => ({
            ...prev,
            [slotKey]: !prev[slotKey],
        }));
    };

    const handleUploadNotaFiscal = async (contratacaoId, file) => {
        if (!canEdit) {
            return;
        }

        if (!contratacaoId || !file) {
            return;
        }

        const token = localStorage.getItem('token');
        const formData = new FormData();
        formData.append('file', file);
        formData.append('pasta', 'freelancerNotaFiscal');

        setUploadingNf((prev) => ({ ...prev, [contratacaoId]: true }));

        try {
            const response = await api({
                method: 'post',
                action: `/eventoFreelancerParticipanteNotaFiscal/${contratacaoId}`,
                token,
                data: formData,
            });

            if (!response.data?.status) {
                toast.error(response.data?.mensagem || 'Erro ao enviar nota fiscal');
                return;
            }

            updateContratacaoArquivos(contratacaoId, response.data.data?.arquivos_nota_fiscal ?? []);
            toast.success('Nota fiscal enviada com sucesso');
        } catch (error) {
            console.log('Erro ao enviar nota fiscal:', error);
            toast.error(error?.response?.data?.mensagem || 'Erro ao enviar nota fiscal');
        } finally {
            setUploadingNf((prev) => ({ ...prev, [contratacaoId]: false }));
        }
    };

    const handleDeleteNotaFiscal = async (contratacaoId, arquivoId) => {
        if (!canEdit) {
            return;
        }

        if (!contratacaoId || !arquivoId) {
            return;
        }

        if (!window.confirm('Remover esta nota fiscal?')) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await api({
                method: 'delete',
                action: `/eventoFreelancerParticipanteNotaFiscal/${contratacaoId}/${arquivoId}`,
                token,
            });

            if (!response.data?.status) {
                toast.error(response.data?.mensagem || 'Erro ao remover nota fiscal');
                return;
            }

            updateContratacaoArquivos(contratacaoId, response.data.data?.arquivos_nota_fiscal ?? []);
            toast.success('Nota fiscal removida');
        } catch (error) {
            console.log('Erro ao remover nota fiscal:', error);
            toast.error(error?.response?.data?.mensagem || 'Erro ao remover nota fiscal');
        }
    };

    const getCategoryFreelancers = (categoryKey) => {
        return Object.entries(selections)
            .filter(([key, value]) => key.startsWith(`${categoryKey}-`) && value)
            .sort((a, b) => {
                const indexA = parseInt(a[0].split('-').pop(), 10) || 0;
                const indexB = parseInt(b[0].split('-').pop(), 10) || 0;

                return indexA - indexB;
            })
            .map(([key, value], index) => ({
                participante: String(value),
                ordem: index + 1,
            }));
    };

    const getCategorySlotCount = (categoryKey, qty) => {
        const selectedIndexes = Object.keys(selections)
            .filter((key) => key.startsWith(`${categoryKey}-`))
            .map((key) => parseInt(key.split('-').pop(), 10))
            .filter((index) => !Number.isNaN(index));

        const highestSelectedIndex = selectedIndexes.length > 0 ? Math.max(...selectedIndexes) + 1 : 0;

        return Math.max(qty, highestSelectedIndex);
    };

    const formatFreelancerOptionLabel = (option, { context }) => {
        return (
            <div className={`gap-1 d-flex align-items-center ${context === 'menu' ? 'justify-content-between' : 'flex-wrap'}`}>
                <span>{option.baseLabel || option.label}</span>
                {option.statusLabel ? (
                    <strong
                        className={`${option.statusClassName || ''}`}
                    >
                        [{option.statusLabel}]
                    </strong>
                ) : null}
            </div>
        );
    };

    const handleSubmit = async () => {
        if (!canEdit) {
            return;
        }

        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            const itens = categories.map((category, catIndex) => {
                const qty = parseInt(category.quantidade_freelancers) || 0;
                const categoryKey = getCategoryKey(category, catIndex);

                return {
                    id: category.id,
                    freelancers: getCategoryFreelancers(categoryKey),
                };
            });

            const response = await api({
                method: 'put',
                action: `/eventoFreelancerParticipante/${eventoId}`,
                token,
                data: itens,
            });

            if (!response.data?.status) {
                toast.error(response.data?.mensagem || 'Erro ao salvar contratação');
                return;
            }

            toast.success('Contratação salva com sucesso');
            onClose();
        } catch (error) {
            console.log('Erro ao salvar contratação:', error);
            toast.error(error?.response?.data?.mensagem || 'Erro ao salvar contratação');
        } finally {
            setLoading(false);
        }
    };

    if (initialLoading) {
        return (
            <div className="text-center p-4">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Carregando...</span>
                </div>
                <p className="mt-2">Carregando dados...</p>
            </div>
        );
    }

    if (categories.length === 0) {
        return (
            <div className="contratacao-form__empty alert alert-warning mb-0 text-center py-4 rounded-4">
                <p className="fw-semibold mb-3" style={{ fontSize: '1.1rem' }}>
                    Nenhuma solicitação encontrada.
                </p>
                <p className="mb-0">
                    O responsável precisa enviar uma solicitação via "Solicitar Freelancer" primeiro.
                </p>
            </div>
        );
    }

    return (
        <div>
            {/* Resumo da Solicitação */}
            <div className="card mb-3">
                <div className="card-body">
                    <h6 className="card-title mb-3">
                        <FaClipboardList className="me-2" />
                        Resumo da Solicitação
                    </h6>
                    <div className="row mb-2">
                        <div className="col-sm-3">
                            <small className="text-muted d-block">Data Início</small>
                            <span className="fw-semibold">{formatDate(eventoData.data) || '-'}</span>
                        </div>
                        <div className="col-sm-3">
                            <small className="text-muted d-block">Data Fim</small>
                            <span className="fw-semibold">{formatDate(eventoData.dataFim) || '-'}</span>
                        </div>
                        <div className="col-sm-3">
                            <small className="text-muted d-block">Categorias</small>
                            <span className="fw-semibold">{categories.length}</span>
                        </div>
                        <div className="col-sm-3">
                            <small className="text-muted d-block">Total Freelancers</small>
                            <span className="fw-semibold text-primary">{getTotalFreelancers()}</span>
                        </div>
                    </div>
                    <div>
                        {categories.map((cat, i) => (
                            <span key={i} className="badge bg-primary me-2 mb-1">
                                {getCategoryTitle(cat, i)} - {cat.quantidade_freelancers || 0}
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            {!canEdit && (
                <div className="alert alert-secondary mb-3 py-2">
                    <small>Visualização somente leitura.</small>
                </div>
            )}

            {/* Categorias */}
            {categories.map((category, catIndex) => {
                const qty = parseInt(category.quantidade_freelancers) || 0;
                const categoryKey = getCategoryKey(category, catIndex);
                const totalSlots = getCategorySlotCount(categoryKey, qty);
                return (
                    <div key={catIndex} className="card mb-3">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start flex-wrap mb-3">
                                <div className="pr-3">
                                    <div className="d-flex flex-wrap align-items-center gap-2">
                                        <span className="font-weight-bold text-primary">
                                            {getCategoryTitle(category, catIndex)}
                                        </span>
                                        {category.descricao_servico ? (
                                            <small className="text-muted ml-2">{category.descricao_servico}</small>
                                        ) : null}
                                    </div>
                                    <div className="small text-muted mt-2 d-flex flex-wrap align-items-center gap-2">
                                        <span className="mr-2 mb-1">{getCategoryPeriod(category)}</span>
                                        {category.staff_local === 'sim' && (
                                            <span className="badge badge-pill badge-light text-primary border mr-2 mb-1">Staff Local</span>
                                        )}
                                        {category.bilingue === 'sim' && (
                                            <span className="badge badge-pill badge-light text-dark border mr-2 mb-1">Bilíngue</span>
                                        )}
                                        {category.reembolso_alimentacao_transporte === 'sim' && (
                                            <span className="badge badge-pill badge-light text-success border mr-2 mb-1">Reembolso</span>
                                        )}
                                    </div>
                                </div>
                                <span className="badge badge-pill badge-light text-primary border px-3 py-2 mb-1">
                                    {qty} freelancers
                                </span>
                            </div>

                            {/* Freelancer slots */}
                            {Array.from({ length: totalSlots }).map((_, slotIndex) => {
                                const slotKey = `${categoryKey}-${slotIndex}`;
                                const selectedFreelancer = selections[slotKey];
                                const contratacao = getFreelancerContratacao(category, categoryKey, slotIndex);
                                const arquivosNotaFiscal = contratacao?.arquivos_nota_fiscal ?? [];
                                const expanded = !!expandedFreelancers[slotKey];

                                return (
                                    <div key={slotIndex} className="mb-2">
                                        <div className="row align-items-center">
                                            <div className="col-auto">
                                                <label className="form-label mb-0 fw-semibold" style={{ minWidth: '100px' }}>
                                                    Freelancer {slotIndex + 1}:
                                                </label>
                                            </div>
                                            <div className="col">
                                                {(() => {
                                                    const options = getFreelancerOptions(category, categoryKey, slotIndex);

                                                    return (
                                                        <ReactSelect
                                                            isClearable
                                                            placeholder="Selecionar freelancer"
                                                            options={options}
                                                            value={getSelectedFreelancerOption(categoryKey, slotIndex, options)}
                                                            onChange={(option) => handleSelectionChange(categoryKey, slotIndex, option?.value || '', qty, category)}
                                                            formatOptionLabel={formatFreelancerOptionLabel}
                                                            className="w-100"
                                                            readOnly={!canEdit}
                                                        />
                                                    );
                                                })()}
                                            </div>
                                            <div className="col-auto">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary btn-sm"
                                                    disabled={!selectedFreelancer}
                                                    onClick={() => toggleFreelancerExpanded(slotKey)}
                                                    title="Notas fiscais"
                                                >
                                                    {expanded ? <FaChevronUp /> : <FaChevronDown />}
                                                </button>
                                            </div>
                                        </div>

                                        {expanded && (
                                            <div
                                                className="ml-0 mt-2 rounded border p-2"
                                            >
                                                {category.arquivos_nota_fiscal_disponivel === false ? (
                                                    <small className="text-muted">
                                                        Upload de notas fiscais indisponível.
                                                    </small>
                                                ) : !contratacao?.id ? (
                                                    <small className="text-muted">
                                                        {canEdit ? 'Salve a contratação antes de anexar notas fiscais.' : 'Contratação ainda não salva.'}
                                                    </small>
                                                ) : (
                                                    <>
                                                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                                                            <strong className="small mb-0">Notas fiscais</strong>
                                                            {canEdit && (
                                                                <label
                                                                    className={`btn btn-outline-primary btn-sm mb-0 d-inline-flex align-items-center justify-content-center ${uploadingNf[contratacao.id] ? 'disabled' : ''}`}
                                                                    title="Adicionar nota fiscal"
                                                                >
                                                                    <FaPlus className="me-1" />
                                                                    {uploadingNf[contratacao.id] ? 'Enviando...' : 'Adicionar'}
                                                                    <input
                                                                        type="file"
                                                                        className="d-none"
                                                                        disabled={!!uploadingNf[contratacao.id]}
                                                                        onChange={(event) => {
                                                                            const file = event.target.files?.[0];
                                                                            event.target.value = '';
                                                                            handleUploadNotaFiscal(contratacao.id, file);
                                                                        }}
                                                                    />
                                                                </label>
                                                            )}
                                                        </div>

                                                        {arquivosNotaFiscal.length === 0 ? (
                                                            <small className="text-muted">Nenhuma nota fiscal anexada.</small>
                                                        ) : (
                                                            <div className="list-group list-group-flush">
                                                                {arquivosNotaFiscal.map((arquivo) => (
                                                                    <div key={arquivo.id} className="list-group-item px-0 py-2 d-flex align-items-center justify-content-between bg-transparent">
                                                                        <span className="small text-truncate mr-2">{arquivo.nome || `Arquivo ${arquivo.id}`}</span>
                                                                        <div className="d-flex align-items-center gap-1">
                                                                            <a
                                                                                className="btn btn-outline-primary btn-sm"
                                                                                href={`${process.env.REACT_APP_API_URL}/arquivo/download/${arquivo.id}`}
                                                                                target="_blank"
                                                                                rel="noreferrer"
                                                                                title="Baixar nota fiscal"
                                                                            >
                                                                                <FaDownload />
                                                                            </a>
                                                                            {canEdit && (
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-outline-danger btn-sm"
                                                                                    onClick={() => handleDeleteNotaFiscal(contratacao.id, arquivo.id)}
                                                                                    title="Remover nota fiscal"
                                                                                >
                                                                                    <FaTrash />
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}

            {/* Legenda */}
            <div className="alert alert-info mb-3 py-2">
                <small>
                    <strong className="text-warning">[INDISPONÍVEL]</strong> = alocado em outro evento no mesmo período
                    {' | '}
                    <strong className="text-danger">[INATIVO]</strong> = cadastro desativado
                </small>
            </div>

            {/* Botões de Ação */}
            <div className="d-flex justify-content-center gap-2">
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onClose}
                    disabled={loading}
                >
                    <FaTimes className="me-2" />
                    Fechar
                </button>
                {canEdit && (
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        <FaPaperPlane className="me-2" />
                        {loading ? 'Enviando...' : 'Enviar Lista ao Responsável'}
                    </button>
                )}
            </div>
        </div>
    );
}
